import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Edit2, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/hooks/useAuth';

export default function TeamMembersDialog({ open, onClose, team }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: ''
  });

  useEffect(() => {
    if (open && team) {
      fetchMembers();
    }
  }, [open, team]);

  const fetchMembers = async () => {
    try {
      const response = await api.get(`/teams/${team.id}/members`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/teams/${team.id}/members`, formData);
      toast.success('Team member added successfully');
      setFormData({ name: '', email: '', role: '' });
      setIsAdding(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (memberId) => {
    setLoading(true);
    try {
      await api.put(`/teams/${team.id}/members/${memberId}`, formData);
      toast.success('Team member updated successfully');
      setFormData({ name: '', email: '', role: '' });
      setEditingId(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (window.confirm('Are you sure you want to remove this team member?')) {
      try {
        await api.delete(`/teams/${team.id}/members/${memberId}`);
        toast.success('Team member removed successfully');
        fetchMembers();
      } catch (error) {
        toast.error('Failed to remove member');
      }
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setFormData({
      name: member.name,
      email: member.email,
      role: member.role || ''
    });
    setIsAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({ name: '', email: '', role: '' });
  };

  if (!team) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="team-members-dialog" aria-describedby="team-members-description">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0f172a] flex items-center gap-2">
            <Users size={24} />
            {team.name} - Team Members
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Members List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {members.length === 0 && !isAdding && (
              <div className="text-center py-8 text-slate-500">
                <Users size={48} className="mx-auto mb-2 opacity-50" />
                <p>No team members yet</p>
                <p className="text-sm">Add your first team member below</p>
              </div>
            )}

            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50"
                data-testid={`team-member-${member.id}`}
              >
                {editingId === member.id ? (
                  <div className="flex-1 space-y-2">
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Name"
                      data-testid="edit-member-name-input"
                    />
                    <Input
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Email"
                      type="email"
                      data-testid="edit-member-email-input"
                    />
                    <Input
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="Role (Optional)"
                      data-testid="edit-member-role-input"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdate(member.id)}
                        disabled={loading}
                        className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                        data-testid="save-member-button"
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEdit}
                        data-testid="cancel-edit-member-button"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <p className="font-semibold text-[#0f172a]" data-testid="member-name">{member.name}</p>
                      <p className="text-sm text-slate-600" data-testid="member-email">{member.email}</p>
                      {member.role && (
                        <p className="text-sm text-slate-500 mt-1" data-testid="member-role">
                          Role: {member.role}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(member)}
                        data-testid={`edit-member-${member.id}-button`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(member.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-member-${member.id}-button`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Add New Form */}
            {isAdding && (
              <form onSubmit={handleAdd} className="p-4 border-2 border-dashed border-[#2563eb] rounded-lg bg-blue-50/50">
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="add-member-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      data-testid="add-member-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role (Optional)</Label>
                    <Input
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="Team Lead, Developer, etc."
                      data-testid="add-member-role-input"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      data-testid="submit-add-member-button"
                    >
                      {loading ? 'Adding...' : 'Add Member'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelEdit}
                      data-testid="cancel-add-member-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Add Button */}
          {!isAdding && !editingId && members.length < 5 && (
            <Button
              variant="outline"
              className="w-full border-dashed border-2 border-[#2563eb] text-[#2563eb] hover:bg-blue-50"
              onClick={() => setIsAdding(true)}
              data-testid="add-team-member-button"
            >
              <Plus size={20} className="mr-2" />
              Add Team Member
            </Button>
          )}

          {members.length >= 5 && !isAdding && (
            <p className="text-sm text-slate-500 text-center">
              Maximum of 5 team members reached
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} data-testid="close-members-dialog-button">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
